import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ClipboardCheck, Clock, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { fromZonedTime } from 'date-fns-tz';
import { useShopTimezone } from '@/contexts/TimezoneContext';

interface Props {
  shopId: string;
  technicianProfileId?: string;
  startDate: string;
  endDate: string;
}

interface InspectionStats {
  count: number;
  bookHours: number;
  actualHours: number;
  efficiency: number;
}

export function InspectionPerformanceCard({ shopId, technicianProfileId, startDate, endDate }: Props) {
  const { shopTimezone } = useShopTimezone();
  const { data: stats, isLoading } = useQuery({
    queryKey: ['inspection-performance', shopId, technicianProfileId, startDate, endDate, shopTimezone],
    queryFn: async (): Promise<InspectionStats> => {
      // KPI-R71-FIX (HIGH-5): previously passed shop-local date strings
      // (e.g. "2024-12-01" from `getDateRange`) directly to .gte/.lte on
      // the `completed_at` timestamptz — Postgres casts these as UTC
      // midnight, so a PST shop's Dec 1 window actually covered Nov 30
      // 16:00 UTC → Dec 1 15:59 UTC (i.e., the LAST 8 hours of Nov 30 PST
      // plus the first 16 hours of Dec 1 PST). That off-by-offset-hours
      // drift is the classic cross-view trust break — same KPI, wrong
      // rows. Convert through shop TZ so the window aligns with the
      // shop's calendar day, mirroring the sibling performance cards.
      const startUtc = fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${endDate}T23:59:59`, shopTimezone).toISOString();
      let query = supabase
        .from('ticket_workflow_inspections' as any)
        .select('book_time_min, total_duration_seconds, completed_at, performed_by')
        .eq('shop_id', shopId)
        .not('completed_at', 'is', null)
        .gte('completed_at', startUtc)
        .lte('completed_at', endUtc);

      if (technicianProfileId) {
        // Resolve technician_profile_id -> user_id via technicians + technician_users
        const { data: techRow } = await supabase
          .from('technicians')
          .select('id')
          .eq('technician_profile_id', technicianProfileId)
          .limit(1)
          .maybeSingle();

        if (techRow) {
          const { data: tuRow } = await supabase
            .from('technician_users')
            .select('user_id')
            .eq('technician_id', techRow.id)
            .limit(1)
            .maybeSingle();

          if (tuRow?.user_id) {
            query = query.eq('performed_by', tuRow.user_id);
          }
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      const rows = (data as any[]) || [];
      const count = rows.length;
      const bookMinutes = rows.reduce((sum: number, r: any) => sum + (r.book_time_min || 0), 0);
      const actualSeconds = rows.reduce((sum: number, r: any) => sum + (r.total_duration_seconds || 0), 0);

      const bookHours = bookMinutes / 60;
      const actualHours = actualSeconds / 3600;
      // KPI-R71-FIX (HIGH-3): efficiency is `book / actual` across the rest of
      // the system (TechnicianPerformanceCard, JobCategoryPerformance,
      // useDirectTechnicianMetrics, analyticsHelpers.calculateEfficiency) —
      // values >100% = faster than book, the convention users learn. This
      // file previously inverted the formula to `actual / book` and still
      // labelled the cell "Efficiency Ratio", so the same technician
      // showed 92% on Passport and 110% here for the same data. The color
      // bands at the render site (≤1 green, >1.25 red) were written for
      // the inverted sense; align those with the standard formula too.
      const efficiency = actualHours > 0 ? bookHours / actualHours : 0;

      return { count, bookHours, actualHours, efficiency };
    },
    enabled: !!shopId,
  });

  if (isLoading || !stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Inspection Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  // KPI-R71-FIX (HIGH-3): color bands flipped to match the standard
  // efficiency convention (book / actual): ≥1.0 means faster than or on
  // book time → green; 0.8–1.0 → yellow; <0.8 → red. This mirrors
  // TechnicianPerformanceCard's getEfficiencyBadge thresholds.
  const efficiencyColor = stats.efficiency >= 1 ? 'text-green-600' : stats.efficiency >= 0.8 ? 'text-yellow-600' : 'text-red-600';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" />
          Inspection Performance
        </CardTitle>
        <CardDescription>Workflow inspection metrics for selected period</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-3 bg-muted/30 rounded-lg text-center">
          <div className="text-2xl font-bold">{stats.count}</div>
          <div className="text-xs text-muted-foreground">Inspections</div>
        </div>
        <div className="p-3 bg-muted/30 rounded-lg text-center">
          <div className="text-2xl font-bold flex items-center justify-center gap-1">
            <Clock className="h-4 w-4" />
            {stats.bookHours.toFixed(1)}h
          </div>
          <div className="text-xs text-muted-foreground">Book Time</div>
        </div>
        <div className="p-3 bg-muted/30 rounded-lg text-center">
          <div className="text-2xl font-bold flex items-center justify-center gap-1">
            <Clock className="h-4 w-4" />
            {stats.actualHours.toFixed(1)}h
          </div>
          <div className="text-xs text-muted-foreground">Actual Time</div>
        </div>
        <div className="p-3 bg-muted/30 rounded-lg text-center">
          <div className={`text-2xl font-bold flex items-center justify-center gap-1 ${efficiencyColor}`}>
            <TrendingUp className="h-4 w-4" />
            {(stats.efficiency * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-muted-foreground">Efficiency Ratio</div>
        </div>
      </CardContent>
    </Card>
  );
}
